import {
    CompanyInformation,
    LocationInformation,
    PersonalInformation,
    Preferences,
} from '@account/routes/AccountDetails/accordions';
import { AccountDetailsSchema } from '@account/schemas';
import { AccountDetailsFormValues } from '@account/types';
import { PlusAccordion } from '@components/display/accordions/PlusAccordion';
import { Button } from '@components/utility/buttons';
import { useAppDispatch } from '@hooks/generic';
import { useDirtyFormik } from '@hooks/useDirtyFormik';
import { useScrollIntoError } from '@hooks/useScrollIntoError';
import useScrollIntoFocus from '@hooks/useScrollIntoFocus';
import { AccordionDetails, Box, Stack, useMediaQuery } from '@mui/material';
import { useGetMyDetailsQuery, usePutMyDetailsMutation } from '@services/accounts';
import { useGetLanguagesQuery } from '@services/translation';
import { changeLocale } from '@store/translationSlice';
import { handleError, toaster } from '@utils/common';
import { FormikProvider, useFormik } from 'formik';
import { ComponentType, useMemo, useState } from 'react';
import { createIntl, createIntlCache, defineMessages, FormattedMessage, useIntl } from 'react-intl';

const intlCache = createIntlCache();

const ACCORDION_MESSAGES = defineMessages({
    personalInfo: { id: 'account.details.personalInfo', defaultMessage: 'Personal Information' },
    company: { id: 'account.details.company', defaultMessage: 'Company Information' },
    location: { id: 'account.details.location', defaultMessage: 'Location Information' },
    preferences: { id: 'account.details.preferences', defaultMessage: 'Preferences' },
});

const ACCORDION_FIELDS = {
    personalInfo: ['first_name', 'last_name', 'email', 'role', 'mobile_phone', 'telephone', 'fax'],
    company: ['company_name', 'job_title', 'job_title_other'],
    location: ['address1', 'address2', 'country_code', 'state_code', 'city'],
    preferences: ['receive_mailings_targeted_at_professionals', 'language', 'food_preference'],
} satisfies Record<string, (keyof AccountDetailsFormValues)[]>;

type AccordionKey = keyof typeof ACCORDION_FIELDS;

const ACCORDION_SECTIONS: {
    key: AccordionKey;
    Content: ComponentType<{ loading?: boolean }>;
}[] = [
    {
        key: 'personalInfo',
        Content: PersonalInformation,
    },
    {
        key: 'company',
        Content: CompanyInformation,
    },
    {
        key: 'location',
        Content: LocationInformation,
    },
    {
        key: 'preferences',
        Content: Preferences,
    },
];

const DirtyFormikListener = () => {
    useDirtyFormik();
    return null;
};

export const AccountDetails = () => {
    //#region State
    const [accordions, setAccordions] = useState<Record<AccordionKey, boolean>>({
        personalInfo: true,
        company: false,
        location: false,
        preferences: false,
    });
    //#endregion

    //#region Data Fetching
    const { data: myDetails, isFetching: areMyDetailsFetching } = useGetMyDetailsQuery();
    const [putMyDetails, { isLoading: isUpdatingMyDetails }] = usePutMyDetailsMutation();
    const { data: languages = [] } = useGetLanguagesQuery();
    //#endregion

    //#region Hooks
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const validationSchema = useMemo(() => AccountDetailsSchema(intl), [intl]);
    const isBelowMd = useMediaQuery((theme) => theme.breakpoints.down('md'));

    const initialValues = useMemo<AccountDetailsFormValues>(
        () => ({
            first_name: myDetails?.first_name ?? '',
            last_name: myDetails?.last_name ?? '',
            email: myDetails?.email ?? '',
            role: myDetails?.role ?? '',
            mobile_phone: myDetails?.mobile_phone ?? '',
            telephone: myDetails?.telephone ?? '',
            fax: myDetails?.fax ?? '',
            company_name: myDetails?.company_name ?? '',
            job_title: myDetails?.job_title ?? '',
            job_title_other: myDetails?.job_title_other ?? '',
            address1: myDetails?.address1 ?? '',
            address2: myDetails?.address2 ?? '',
            country_code: myDetails?.country?.code ?? '',
            state_code: myDetails?.state_code ?? '',
            city: myDetails?.city ?? '',
            receive_mailings_targeted_at_professionals: myDetails?.receive_mailings_targeted_at_professionals ?? 0,
            language: myDetails?.language ?? '',
            food_preference: myDetails?.food_preference ?? '',
        }),
        [myDetails]
    );

    const formik = useFormik<AccountDetailsFormValues>({
        initialValues,
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                await putMyDetails(values).unwrap();
                let successIntl = intl;
                if (values.language && values.language !== (myDetails?.language ?? '')) {
                    const languageName = languages.find(({ value }) => value === values.language)?.label;
                    const localeChange = await dispatch(
                        changeLocale({ locale: values.language, localeName: languageName })
                    );
                    // To show the success message in the newly selected language
                    if (changeLocale.fulfilled.match(localeChange) && localeChange.payload) {
                        successIntl = createIntl(
                            {
                                locale: localeChange.payload.locale,
                                defaultLocale: 'en',
                                messages: localeChange.payload.messages ?? {},
                            },
                            intlCache
                        );
                    }
                }
                toaster.success({
                    title: successIntl.formatMessage({
                        id: 'account.details.updateSuccess',
                        defaultMessage: 'Account details updated successfully',
                    }),
                    msg: successIntl.formatMessage({
                        id: 'account.details.updateSuccessMsg',
                        defaultMessage: 'Your account details have been updated.',
                    }),
                });
            } catch (error) {
                handleError('Error updating account details', error);
            }
        },
    });

    useScrollIntoError({
        formik,
        accordions: { state: [accordions, setAccordions], fieldsPerAccordion: ACCORDION_FIELDS },
    });
    useScrollIntoFocus({
        accordions: { state: [accordions, setAccordions], fieldsPerAccordion: ACCORDION_FIELDS },
    });
    //#endregion

    //#region Handlers
    const toggleAccordion = (key: AccordionKey) => {
        setAccordions((previous) => ({
            ...previous,
            [key]: !previous[key],
        }));
    };
    //#endregion

    return (
        <FormikProvider value={formik}>
            <title>Edit your account - Victron Professional</title>
            <DirtyFormikListener />
            <Box
                component="form"
                bgcolor="#fff"
                className="black-border"
                borderRadius="5px"
                p="24px"
                noValidate
                autoComplete="off"
                onSubmit={formik.handleSubmit}
            >
                {ACCORDION_SECTIONS.map(({ key, Content }) => (
                    <PlusAccordion
                        key={key}
                        expanded={accordions[key]}
                        onChange={() => toggleAccordion(key)}
                        summary={intl.formatMessage(ACCORDION_MESSAGES[key])}
                    >
                        <AccordionDetails className="py-[24px] grid md:grid-cols-2 gap-x-[20px] gap-y-[24px]">
                            <Content loading={areMyDetailsFetching || isUpdatingMyDetails} />
                        </AccordionDetails>
                    </PlusAccordion>
                ))}
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    alignItems="center"
                    justifySelf={{ md: 'end' }}
                    mt="24px"
                    gap="15px"
                >
                    <Button.Neutral
                        fullWidth={isBelowMd}
                        size="large"
                        sx={{ bgcolor: 'neutral-650.main' }}
                        onClick={formik.handleReset}
                        disabled={!formik.dirty}
                        className="font-semibold"
                    >
                        <FormattedMessage id="common.revertChanges" defaultMessage="Revert Changes" />
                    </Button.Neutral>
                    <Button
                        size="large"
                        type="submit"
                        fullRounded
                        fullWidth={isBelowMd}
                        loading={isUpdatingMyDetails}
                        disabled={isUpdatingMyDetails}
                        loadingPosition="start"
                        className="font-semibold"
                    >
                        <FormattedMessage id="common.saveChanges" defaultMessage="Save Changes" />
                    </Button>
                </Stack>
            </Box>
        </FormikProvider>
    );
};
